import React from "react";
import { Button } from "@/components";
import { Bot, Layers, Megaphone, Target, AlertCircle, Share2 } from "lucide-react";
import type { ContentStrategyConfig, ContentStrategyStatus, ContentStrategyMode } from "../interfaces";

interface ResolvedEntities {
  agents: { id: number; name: string; imageUrl?: string }[];
  brands: { id: number; title: string; imageUrl?: string }[];
  campaigns: { id: number; title: string; imageUrl?: string }[];
  integrations: { id: number; label: string }[];
}

interface StrategyReviewStepProps {
  planName: string;
  planDescription?: string;
  config: {
    status: ContentStrategyStatus;
    mode: ContentStrategyMode;
    config: ContentStrategyConfig;
  };
  agentIds: number[];
  brandIds: number[];
  campaignIds: number[];
  integrationIds: number[];
  resolvedEntities: ResolvedEntities;
  onBack: () => void;
  onCreate: () => void;
  isLoading?: boolean;
  validationError?: string;
  /** When provided (e.g. "Update plan"), used for submit button instead of "Create plan" */
  submitLabel?: string;
}

export const StrategyReviewStep: React.FC<StrategyReviewStepProps> = ({
  planName,
  planDescription,
  config,
  agentIds,
  brandIds,
  campaignIds,
  integrationIds,
  resolvedEntities,
  onBack,
  onCreate,
  isLoading = false,
  validationError,
  submitLabel = "Create plan",
}) => {
  const duration =
    config.config?.duration &&
    `${config.config.duration.periodCount} ${config.config.duration.period}`;

  const hasSelection =
    agentIds.length > 0 ||
    brandIds.length > 0 ||
    campaignIds.length > 0 ||
    integrationIds.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Review & Finalize</h2>
        <p className="text-muted-foreground mt-2">
          Review your configuration and selections before creating the content plan.
        </p>
      </div>

      {validationError && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{validationError}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Config summary */}
        <div className="border border-input rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-5 w-5" />
            <h3 className="font-semibold">Configuration</h3>
          </div>
          <div className="mb-3 pb-3 border-b border-input">
            <div className="mb-1">
              <dt className="text-muted-foreground text-xs">Name</dt>
              <dd className="font-medium">{planName}</dd>
            </div>
            {planDescription && (
              <div>
                <dt className="text-muted-foreground text-xs">Description</dt>
                <dd className="text-sm">{planDescription}</dd>
              </div>
            )}
          </div>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <dt className="text-muted-foreground">Status</dt>
              <dd>{config.status}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Mode</dt>
              <dd>{config.mode}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Duration</dt>
              <dd>{duration}</dd>
            </div>
          </dl>
          {config.config?.demographics &&
            ((config.config.demographics.ageGroups?.length ?? 0) > 0 ||
              (config.config.demographics.regions?.length ?? 0) > 0 ||
              (config.config.demographics.ethnicities?.length ?? 0) > 0 ||
              (config.config.demographics.interests?.length ?? 0) > 0) && (
              <div className="mt-3 pt-3 border-t border-input">
                <dt className="text-muted-foreground text-xs">Demographics</dt>
                <dd className="text-sm mt-1">
                  {[
                    config.config.demographics.ageGroups?.join(", "),
                    config.config.demographics.regions?.join(", "),
                    config.config.demographics.ethnicities?.join(", "),
                    config.config.demographics.interests?.join(", "),
                  ]
                    .filter(Boolean)
                    .join(" • ")}
                </dd>
              </div>
            )}
        </div>

        {/* Agents */}
        <div className="border border-input rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Bot className="h-5 w-5" />
            <h3 className="font-semibold">Agents ({agentIds.length})</h3>
            {agentIds.length === 0 && (
              <span className="text-xs text-destructive">Required</span>
            )}
          </div>
          {resolvedEntities.agents.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {resolvedEntities.agents.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 text-sm"
                >
                  {a.imageUrl && (
                    <img
                      src={a.imageUrl}
                      alt=""
                      className="w-6 h-6 rounded object-cover"
                    />
                  )}
                  {a.name}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">None selected</p>
          )}
        </div>

        {/* Brands */}
        <div className="border border-input rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="h-5 w-5" />
            <h3 className="font-semibold">Brands ({brandIds.length})</h3>
            {brandIds.length === 0 && (
              <span className="text-xs text-destructive">Required</span>
            )}
          </div>
          {resolvedEntities.brands.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {resolvedEntities.brands.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 text-sm"
                >
                  {b.imageUrl && (
                    <img
                      src={b.imageUrl}
                      alt=""
                      className="w-6 h-6 rounded object-cover"
                    />
                  )}
                  {b.title}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">None selected</p>
          )}
        </div>

        {/* Campaigns */}
        <div className="border border-input rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Megaphone className="h-5 w-5" />
            <h3 className="font-semibold">Campaigns ({campaignIds.length})</h3>
            {campaignIds.length === 0 && (
              <span className="text-xs text-destructive">Required</span>
            )}
          </div>
          {resolvedEntities.campaigns.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {resolvedEntities.campaigns.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 text-sm"
                >
                  {c.imageUrl && (
                    <img
                      src={c.imageUrl}
                      alt=""
                      className="w-6 h-6 rounded object-cover"
                    />
                  )}
                  {c.title}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">None selected</p>
          )}
        </div>

        {/* Integrations */}
        <div className="border border-input rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Share2 className="h-5 w-5" />
            <h3 className="font-semibold">Integrations ({integrationIds.length})</h3>
            {integrationIds.length === 0 && (
              <span className="text-xs text-destructive">Required</span>
            )}
          </div>
          {resolvedEntities.integrations?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {resolvedEntities.integrations.map((i) => (
                <div
                  key={i.id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/50 text-sm"
                >
                  {i.label}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">None selected</p>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={onCreate}
          disabled={isLoading || !hasSelection}
        >
          {isLoading
            ? (submitLabel === "Update plan" ? "Updating..." : "Creating...")
            : submitLabel}
        </Button>
      </div>
    </div>
  );
};
